import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Info, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { TokenLogo } from "@/components/common/TokenLogo";
import { TestTokenNote } from "@/components/common/TestTokenNote";
import { EXCHANGE } from "@/constants/testIds";
import { EXPAND } from "@/lib/motion";
import { minReceived, SLIPPAGE_PERCENT } from "@/lib/poolSwap";

const LIVE_BADGE = "mt-2 bg-green-500/10 text-green-400 border border-green-500/30";
const BOX = "p-4 rounded-xl bg-[#050505] border border-[#2A2411]";

export const TokenIcon = ({ symbol }) => <TokenLogo symbol={symbol} size="sm" />;

export const TokenPill = ({ symbol }) => (
  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0A0A0A] border border-[#2A2411] shrink-0">
    <TokenIcon symbol={symbol} />
    <span className="text-sm font-medium text-white">{symbol}</span>
  </div>
);

export const TradeHeader = ({ accent, title, subtitle, live, liveTestId, liveLabel = "Live on FSC", demoLabel = "Demo - Simulated" }) => (
  <div className="text-center mb-8">
    <h1 className="font-display text-3xl font-bold tracking-tight mb-2">
      <span className="text-[#D4AF37]">{accent}</span> {title}
    </h1>
    <p className="text-zinc-400 text-sm">{subtitle}</p>
    {live ? <Badge data-testid={liveTestId} className={LIVE_BADGE}>{liveLabel}</Badge> : <Badge className="demo-badge mt-2">{demoLabel}</Badge>}
    <div><TestTokenNote testId="trade-test-token-note" className="mt-3" /></div>
  </div>
);

export const AmountBox = ({ label, amount, onChange, extra, children }) => (
  <div className={BOX}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs text-zinc-500">{label}</span>
      {extra}
    </div>
    <div className="flex items-center gap-3">
      <Input data-testid={EXCHANGE.amountInput} type="number" placeholder="0.0" value={amount} onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border-none text-2xl font-display font-bold text-white placeholder:text-zinc-700 focus:ring-0 p-0 h-auto" />
      {children}
    </div>
  </div>
);

const OutputValue = ({ loading, quote }) => {
  if (loading) return <span className="text-zinc-600">...</span>;
  if (quote) return quote.outputAmount;
  return <span className="text-zinc-700">0.0</span>;
};

export const OutputBox = ({ label, loading, quote, children }) => (
  <div className={BOX}>
    <span className="text-xs text-zinc-500">{label}</span>
    <div className="flex items-center gap-3 mt-2">
      <p data-testid={EXCHANGE.outputAmount} className="text-2xl font-display font-bold text-white flex-1">
        <OutputValue loading={loading} quote={quote} />
      </p>
      {children}
    </div>
  </div>
);

export const QuoteError = ({ error, loading }) =>
  error && !loading ? <p data-testid="exchange-quote-error" className="text-sm text-red-400 px-1">{error}</p> : null;

export const QuoteRow = ({ label, value, testId, strong, valueClass = "text-white font-mono" }) => (
  <div className={`flex justify-between text-sm ${strong ? "font-medium" : ""}`}>
    <span className={strong ? "text-zinc-300" : "text-zinc-400"}>{label}</span>
    <span data-testid={testId} className={valueClass}>{value}</span>
  </div>
);

export const FeeLabel = ({ percent }) => <>Fee (<span data-testid={EXCHANGE.feePercent}>{percent}%</span>)</>;

export const QuoteBreakdown = ({ children }) => (
  <motion.div data-testid={EXCHANGE.quoteBreakdown} {...EXPAND} className={`${BOX} space-y-2`}>
    <div className="flex items-center gap-1 mb-2">
      <Info size={12} className="text-zinc-500" />
      <span className="text-xs text-zinc-500">Quote Breakdown</span>
    </div>
    {children}
  </motion.div>
);

export const QuoteDivider = () => <Separator className="bg-[#2A2411]" />;

/** Pool fee / price impact / min-received rows shown for live router quotes. */
export const PoolDetails = ({ quote, decimals, token, prefix }) => (
  <div className="space-y-1 pt-1 text-xs text-zinc-500">
    <div className="flex justify-between"><span>Pool liquidity fee (included in price)</span><span>{quote.poolFeePercent}%</span></div>
    <div className="flex justify-between"><span>Price impact</span><span data-testid={`${prefix}-price-impact`}>{quote.priceImpact}%</span></div>
    <div className="flex justify-between"><span>Min. received ({SLIPPAGE_PERCENT}% slippage)</span>
      <span data-testid={`${prefix}-min-received`}>{(Number(minReceived(quote)) / 10 ** decimals).toFixed(6)} {token}</span></div>
  </div>
);

const SubmitLabel = ({ executing, step, address, amount, connectLabel, children }) => {
  if (executing) return <span className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> {step || "Processing..."}</span>;
  if (!address) return connectLabel;
  if (!amount) return "Enter Amount";
  return children;
};

export const SubmitButton = ({ testId = EXCHANGE.submitBtn, onClick, disabled, ...labelProps }) => (
  <Button data-testid={testId} onClick={onClick} disabled={disabled}
    className="w-full bg-[#D4AF37] text-black hover:bg-[#F3E5AB] font-semibold h-12 rounded-md transition-transform active:scale-[0.98] disabled:opacity-40">
    <SubmitLabel {...labelProps} />
  </Button>
);

export const TxLink = ({ hash, testId, label, receipt = true }) =>
  hash ? (
    <div className="flex flex-col items-center gap-1">
      {receipt && (
        <Link to={`/receipt/${hash}`} data-testid={`${testId}-receipt`} className="text-sm font-semibold text-[#D4AF37] hover:text-[#F3E5AB] transition-colors">
          View receipt →
        </Link>
      )}
      <Link to={`/tx/${hash}`} data-testid={testId} className="block text-center table-link font-mono text-xs break-all">
        {label}: {hash.slice(0, 18)}…
      </Link>
    </div>
  ) : null;
